// 광고 (§13). 스토어 앱(Capacitor 네이티브) = AdMob 전면/리워드. 웹 = 호출부에서 목/AdSense.
// 실 광고 표시는 cap add ios/android + 기기/시뮬레이터 필요. 웹/dev에선 no-op(목 화면이 대신).
// 실 광고 단위 ID는 .env(VITE_ADMOB_*)로 교체. 없으면 구글 공식 테스트 ID + 테스트 모드.

import { Capacitor, type PluginListenerHandle } from "@capacitor/core";
import { App as CapApp } from "@capacitor/app";
import {
  AdMob,
  AdmobConsentStatus,
  BannerAdPluginEvents,
  BannerAdPosition,
  BannerAdSize,
  InterstitialAdPluginEvents,
  RewardAdPluginEvents,
} from "@capacitor-community/admob";
import { loadAdConfig, adConfig } from "./remoteConfig";

function isNative(): boolean {
  return Capacitor.isNativePlatform();
}
function isIOS(): boolean {
  return Capacitor.getPlatform() === "ios";
}

// 구글 공식 테스트 광고 단위 ID.
const TEST = {
  interstitial: {
    ios: "ca-app-pub-3940256099942544/4411468910",
    android: "ca-app-pub-3940256099942544/1033173712",
  },
  rewarded: {
    ios: "ca-app-pub-3940256099942544/1712485313",
    android: "ca-app-pub-3940256099942544/5224354917",
  },
  banner: {
    ios: "ca-app-pub-3940256099942544/2934735716",
    android: "ca-app-pub-3940256099942544/6300978111",
  },
};

const env = (typeof import.meta !== "undefined" && import.meta.env) || ({} as Record<string, string>);
// 광고 유닛 ID(전면/보상형)는 빌드에 박지 않고 프록시에서 원격으로 받는다(remoteConfig). 미수신이면 테스트 ID 폴백.
// USE_TEST = 개발 중 강제 테스트 모드(VITE_ADMOB_TESTING). 운영 빌드는 false →
//   서버가 내려준 ID가 테스트 ID면 테스트 광고, 실 ID면 실 광고가 뜬다(심사=테스트 / 승인 후=실, 재빌드 불필요).
const USE_TEST = env.VITE_ADMOB_TESTING === "1" || env.VITE_ADMOB_TESTING === "true";

// 앱 ID(~ 포함): 광고 호출엔 안 쓰임(네이티브 config가 읽음). cap add 후 Info.plist/Manifest 주입용으로 노출.
export const ADMOB_APP_ID = {
  ios: env.VITE_ADMOB_APP_ID_IOS || "",
  android: env.VITE_ADMOB_APP_ID_ANDROID || "",
};

// §13: 긴 분노 = 긴(리워드) 광고. 길이 임계는 출발점이고 튜닝 대상.
export const LONG_RANT_CHARS = 280;
export function isLongRant(charCount: number): boolean {
  return charCount >= LONG_RANT_CHARS;
}

// 전면/보상형 ID는 원격(remoteConfig)에서. 없으면 구글 테스트 ID 폴백.
function interstitialId(): string {
  const c = adConfig()?.interstitial;
  const remote = isIOS() ? c?.ios : c?.android;
  return remote || (isIOS() ? TEST.interstitial.ios : TEST.interstitial.android);
}
function rewardedId(): string {
  const c = adConfig()?.rewarded;
  const remote = isIOS() ? c?.ios : c?.android;
  return remote || (isIOS() ? TEST.rewarded.ios : TEST.rewarded.android);
}
function bannerId(): string {
  return isIOS()
    ? env.VITE_ADMOB_BANNER_IOS || TEST.banner.ios
    : env.VITE_ADMOB_BANNER_ANDROID || TEST.banner.android;
}

let initialized = false;
let attInFlight = false; // 재진입/동시실행 방지(성공하면 status로 재확인해 조기 종료)

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/** iOS ATT(앱 추적 투명성) 권한 요청 — AdMob 초기화와 완전 분리. iPad(iPadOS)도 'ios'라 포함.
 *  ★리젝 재발 방지 핵심:
 *   1) 예전엔 getState가 비활성이면 그냥 포기했다 → 콜드 런치는 이미 active라 appStateChange(변화)가
 *      안 터져서 영영 재시도 안 됨 → 프롬프트 미표시(리뷰어 iPad에서 정확히 이 현상).
 *   2) 이제 "앱이 active가 될 때까지 기다렸다가, 프롬프트가 실제로 응답(status가 notDetermined 아님)될
 *      때까지" 반복한다. iOS는 앱이 active일 때만 ATT 모달을 띄우므로 active 확인이 관건.
 *  requestTrackingAuthorization은 active·notDetermined면 모달을 띄우고 사용자가 답할 때까지 대기 후
 *  authorized/denied를 반환한다. 비활성/조기 호출이면 표시 없이 notDetermined 반환 → 잠시 뒤 재시도. */
export async function requestATT(): Promise<void> {
  if (!isNative() || !isIOS() || attInFlight) return;
  attInFlight = true;
  try {
    for (let i = 0; i < 24; i++) {
      const { isActive } = await CapApp.getState().catch(() => ({ isActive: true }));
      if (isActive) {
        // 이미 결정됐으면(승인/거부/제한) 더 볼 것 없음.
        if ((await AdMob.trackingAuthorizationStatus()).status !== "notDetermined") return;
        // active·notDetermined → 모달 표시(사용자 응답까지 대기).
        await AdMob.requestTrackingAuthorization();
        // 응답 완료됐으면 끝. 여전히 notDetermined면 표시가 안 된 것 → 잠깐 뒤 재시도.
        if ((await AdMob.trackingAuthorizationStatus()).status !== "notDetermined") return;
      }
      await sleep(500); // active 대기 / 재시도 간격 (최대 ~12초)
    }
  } catch {
    /* 예외 — 앱 동작엔 영향 없음 */
  } finally {
    attInFlight = false; // 다음 트리거(예: 포그라운드 복귀)에서 다시 시도 가능
  }
}

/** 앱 시작 시 1회: AdMob 초기화 + UMP 동의. (ATT는 requestATT로 분리 — 위 주석 참고.) 웹에선 no-op. */
export async function initAds(): Promise<void> {
  if (!isNative() || initialized) return;
  initialized = true;
  try {
    await loadAdConfig(); // 원격 광고 ID 먼저(실패해도 테스트 ID 폴백)
    await AdMob.initialize({ initializeForTesting: USE_TEST });
    // UMP 동의(EEA/UK 필수, 글로벌 안전).
    const info = await AdMob.requestConsentInfo();
    if (info.isConsentFormAvailable && info.status === AdmobConsentStatus.REQUIRED) {
      await AdMob.showConsentForm();
    }
  } catch {
    /* 초기화 실패해도 앱은 동작(광고만 안 뜸). ATT는 별도(requestATT)라 이 실패와 무관. */
  }
}

let bannerRetryTimer: ReturnType<typeof setTimeout> | null = null;
let bannerFailHandle: PluginListenerHandle | null = null;

async function stopBannerRetry(): Promise<void> {
  if (bannerRetryTimer) {
    clearTimeout(bannerRetryTimer);
    bannerRetryTimer = null;
  }
  if (bannerFailHandle) {
    await bannerFailHandle.remove();
    bannerFailHandle = null;
  }
}

/** 배너 표시(홈 화면, §13). marginTop(CSS px≈dp; 플러그인이 ×density로 px 변환)을 주면 상단 기준
 *  그 위치에 오버레이(노트 바로 아래), 없으면 화면 하단. 적응형 배너. 웹/실패면 no-op.
 *  콜드 스타트·일시적 fill 실패에 대비해 로드 실패 시 재시도(최대 4회). */
export async function showBottomBanner(marginTop = 0): Promise<void> {
  if (!isNative()) return;
  await stopBannerRetry();
  const opts = {
    adId: bannerId(),
    adSize: BannerAdSize.ADAPTIVE_BANNER,
    position: marginTop > 0 ? BannerAdPosition.TOP_CENTER : BannerAdPosition.BOTTOM_CENTER,
    margin: marginTop,
    isTesting: USE_TEST,
  };
  const show = () => {
    AdMob.showBanner(opts).catch(() => {});
  };
  let attempts = 0;
  bannerFailHandle = await AdMob.addListener(BannerAdPluginEvents.FailedToLoad, () => {
    if (attempts >= 4) return;
    attempts += 1;
    bannerRetryTimer = setTimeout(show, 2500);
  });
  show();
}

/** 배너 제거(홈을 벗어날 때). 재시도 타이머·리스너도 정리. */
export async function hideBottomBanner(): Promise<void> {
  if (!isNative()) return;
  await stopBannerRetry();
  try {
    await AdMob.removeBanner();
  } catch {
    /* 이미 없을 수 있음 */
  }
}

/** 전면광고: 준비 → 표시 → 닫힘까지 대기. 웹/실패면 즉시 resolve(루프 안 끊김). */
export async function showInterstitial(): Promise<void> {
  if (!isNative()) return;
  try {
    await AdMob.prepareInterstitial({ adId: interstitialId(), isTesting: USE_TEST });
    const handles: PluginListenerHandle[] = [];
    await new Promise<void>((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        handles.forEach((h) => h.remove());
        clearTimeout(safety);
        resolve();
      };
      const safety = setTimeout(finish, 20000); // 닫힘 이벤트 누락 대비
      Promise.all([
        AdMob.addListener(InterstitialAdPluginEvents.Dismissed, finish),
        AdMob.addListener(InterstitialAdPluginEvents.FailedToShow, finish),
      ]).then((hs) => {
        handles.push(...hs);
        if (settled) hs.forEach((h) => h.remove());
      });
      AdMob.showInterstitial().catch(finish);
    });
  } catch {
    /* 실패해도 진행 */
  }
}

// 리워드 결과: earned=끝까지 봄(보상) · dismissed=중간에 닫음(보상 없음) · unavailable=로드/표시 실패.
// 호출부는 unavailable이면 "게이트 통과"시켜 광고 인프라 문제로 사용자를 막지 않는다(§13).
export type RewardResult = "earned" | "dismissed" | "unavailable";

/** 리워드 광고(export 게이트, §13). */
export async function showRewarded(): Promise<RewardResult> {
  if (!isNative()) return "unavailable";
  try {
    await AdMob.prepareRewardVideoAd({ adId: rewardedId(), isTesting: USE_TEST });
  } catch {
    return "unavailable"; // 로드 실패 → 막지 않음
  }
  const handles: PluginListenerHandle[] = [];
  return await new Promise<RewardResult>((resolve) => {
    let earned = false;
    let shown = false;
    let settled = false;
    const finish = (r: RewardResult) => {
      if (settled) return;
      settled = true;
      handles.forEach((h) => h.remove());
      clearTimeout(safety);
      resolve(r);
    };
    // 표시 자체가 안 됐으면 unavailable, 표시됐는데 닫혔으면 earned/dismissed.
    const safety = setTimeout(() => finish(shown ? (earned ? "earned" : "dismissed") : "unavailable"), 60000);
    Promise.all([
      AdMob.addListener(RewardAdPluginEvents.Rewarded, () => {
        earned = true;
      }),
      AdMob.addListener(RewardAdPluginEvents.Dismissed, () => finish(earned ? "earned" : "dismissed")),
      AdMob.addListener(RewardAdPluginEvents.FailedToShow, () => finish("unavailable")),
    ]).then((hs) => {
      handles.push(...hs);
      if (settled) hs.forEach((h) => h.remove());
    });
    AdMob.showRewardVideoAd()
      .then(() => {
        shown = true;
      })
      .catch(() => finish("unavailable"));
  });
}
